// FILENAME: frontend/src/utils/dataNormalizer.js
export const BRAND_MAP = {
  C: "Chevrolet",
  G: "GMC",
  B: "Buick",
  K: "Cadillac",
};

const NAME_TO_CODE = {
    "Chevrolet": "C",
    "GMC": "G",
    "Buick": "B",
    "Cadillac": "K",
};

const normalizeStatusToken = (value) => {
  if (value === null || value === undefined) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');
};

export const isOptedOutStatus = (status) => {
  const token = normalizeStatusToken(status);
  return token === 'optedout' || token === 'optout';
};

export const isSyntheticCancellation = (row) => {
  const value = row?.syntheticCancellation;
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  return ['true', 'yes', 'y', '1'].includes(String(value).trim().toLowerCase());
};

export const hasSyntheticCancellationState = (row) => {
  const value = row?.syntheticCancellationState;
  if (value === null || value === undefined) return false;
  const token = String(value).trim().toLowerCase();
  return Boolean(token && !['false', 'no', 'n', '0'].includes(token));
};

export const resolveSyntheticCancellationStatus = (row) => {
  if (isSyntheticCancellation(row)) return 'Cancelled';
  if (hasSyntheticCancellationState(row)) return 'Cooling';
  return '';
};

export const isCoolingStatus = (rowOrStatus) => {
  const status = typeof rowOrStatus === 'string' ? rowOrStatus : rowOrStatus?.enrollmentStatusCd || rowOrStatus?.syntheticCancellationState;
  const token = normalizeStatusToken(status);
  return token.startsWith('cooling');
};

export const isSelectionActive = (selection = {}) => {
  const token = normalizeStatusToken(selection?.productSelectionStatus || selection?.status);
  const pendingPrefixes = ['pendingcancel', 'pendingcancellation'];
  const cancelledPrefixes = ['cancelled', 'canceled'];
  if (pendingPrefixes.some((prefix) => token.startsWith(prefix))) {
    return true;
  }
  if (cancelledPrefixes.some((prefix) => token.startsWith(prefix))) {
    return false;
  }
  if (selection?.cancelDate && !pendingPrefixes.some((prefix) => token.startsWith(prefix))) {
    return false;
  }
  return true;
};

const normalizeBrandEntry = (entry) => {
  if (!entry) {
    return { name: null, code: null };
  }

  if (typeof entry === 'string') {
    const trimmed = entry.trim();
    if (!trimmed) return { name: null, code: null };
    const inferredCode =
      trimmed.length === 1
        ? trimmed.toUpperCase()
        : NAME_TO_CODE[trimmed] || null;
    const normalizedName =
      inferredCode && BRAND_MAP[inferredCode] ? BRAND_MAP[inferredCode] : trimmed;
    return {
      name: normalizedName,
      code: inferredCode,
    };
  }

  if (typeof entry === 'object') {
    const nameCandidates = [
      entry.name,
      entry.brandName,
      entry.brand,
      entry.description,
      entry.label,
      entry.brandNm,
      entry.displayName,
    ];
    const codeCandidates = [
      entry.brandCd,
      entry.brandCode,
      entry.code,
      entry.brandCd?.toString?.(),
    ];

    const rawName = nameCandidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    const rawCode = codeCandidates.find((value) => typeof value === 'string' && value.trim().length > 0);

    let code = rawCode ? rawCode.trim().toUpperCase() : null;
    if (code && code.length > 1 && NAME_TO_CODE[code]) {
      code = NAME_TO_CODE[code];
    }

    let name = rawName ? rawName.trim() : null;
    if (!name && code) {
      name = BRAND_MAP[code] || code;
    }
    if (!code && name) {
      code = NAME_TO_CODE[name] || null;
    }

    return {
      name: name || null,
      code: code || null,
    };
  }

  const stringified = String(entry).trim();
  if (!stringified) return { name: null, code: null };
  return {
    name: stringified,
    code: NAME_TO_CODE[stringified] || null,
  };
};

const normalizeBrands = (raw) => {
  const entries = Array.isArray(raw) ? raw : [];
  const namesSet = new Set();
  const codesSet = new Set();

  entries.forEach((entry) => {
    const { name, code } = normalizeBrandEntry(entry);
    if (name) namesSet.add(name);
    if (code) codesSet.add(code);
  });

  // If we have codes but no names, expand using the lookup.
  if (namesSet.size === 0 && codesSet.size > 0) {
    codesSet.forEach((code) => {
      const mapped = BRAND_MAP[code];
      if (mapped) namesSet.add(mapped);
    });
  }

  // If we have names but no codes, infer codes from the names when possible.
  if (codesSet.size === 0 && namesSet.size > 0) {
    namesSet.forEach((name) => {
      const inferred = NAME_TO_CODE[name];
      if (inferred) codesSet.add(inferred);
    });
  }

  const names = Array.from(namesSet);
  const codes = Array.from(codesSet);
  const csv = names.join(', ');

  return {
    names,
    codes,
    csv,
  };
};

export const PROGRAM_CODE_MAP = {
  Website: 'DDSDWC',
  Chat: 'DDSCTP',
  Trade: 'DDSTTCBG',
  SEO: 'DDSSEO',
  'Digital Ad Sales': 'DAP',
  'Digital Ad Service': 'DAS',
};

export const CODE_PROGRAM_MAP = Object.fromEntries(
  Object.entries(PROGRAM_CODE_MAP).map(([name, code]) => [code, name])
);

const normalizeProgramToken = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

/**
 * Resolve an arbitrary program identifier (short name, code, or long GM display name)
 * into one of our canonical program names (Website, Chat, Trade, SEO, ...).
 *
 * Returns null when it cannot be confidently resolved.
 */
export const resolveProgramName = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // Exact match (canonical name)
  if (PROGRAM_CODE_MAP[trimmed]) return trimmed;

  // Code match (DDSDWC, DDSCTP, ...)
  if (CODE_PROGRAM_MAP[trimmed]) return CODE_PROGRAM_MAP[trimmed];

  const token = normalizeProgramToken(trimmed);
  if (!token) return null;

  // Prefer specific matches before generic ones.
  if (
    token.includes('websiteofchoice') ||
    token.includes('website') ||
    token.includes('dwc') ||
    token === 'web' ||
    token === 'dlrwst'
  ) {
    return 'Website';
  }
  if ((token.includes('digad') && token.includes('sales')) || token === 'dga') return 'Digital Ad Sales';
  if ((token.includes('digad') && token.includes('service')) || token === 'sdga') return 'Digital Ad Service';
  if (token.includes('digitaladservice') || token === 'das') return 'Digital Ad Service';
  if (token.includes('digitaladsales') || token === 'dap') return 'Digital Ad Sales';
  if (token.includes('trade') || token.includes('traffic') || token.includes('ttc') || token === 'trtl') {
    return 'Trade';
  }
  if (token.includes('seo')) return 'SEO';
  if (token.includes('chat') || token.includes('ctp')) return 'Chat';

  return null;
};

/**
 * Resolve an arbitrary program identifier into a program code (DDSDWC, DAP, ...).
 * Returns null when it cannot be resolved.
 */
export const resolveProgramCode = (value) => {
  const name = resolveProgramName(value);
  return name ? PROGRAM_CODE_MAP[name] : null;
};

export function normalizeProgramEnrollmentRows(api) {
  const rawProgramCode = api.programCd ?? '';
  const programDisplayName = api.programName ?? '';
  const resolvedProgramName = resolveProgramName(rawProgramCode) || resolveProgramName(programDisplayName) || null;
  const programCode = rawProgramCode || (resolvedProgramName ? PROGRAM_CODE_MAP[resolvedProgramName] : '');
  const programShortName = resolvedProgramName || CODE_PROGRAM_MAP[programCode] || programDisplayName || '';

  return (api.enrollmentReports ?? []).map(r => {
    const { names: brandNames, codes: brandCodes, csv: brandsCsv } = normalizeBrands(r.brandName);
    
    const latestCancel = (r.productSelections ?? [])
        .map(p => p.cancelDate)
        .filter(d => !!d)
        .sort()
        .pop() ?? null;

    const terminationDate = r.isTerminatedDate ?? latestCancel ?? r.optOutDt ?? null;
    
    const hasLiveSelections = (r.productSelections ?? []).some(isSelectionActive);

    const programLabel = programDisplayName || programShortName || '';

    return {
      ...r,
      dealerId: r.dealerCd ?? '',
      program: programCode,
      programCode,
      programName: programShortName || programLabel,
      programLabel,
      programStatusCd: r.enrollmentStatusCd ?? '',
      lastUpdateDate: r.lastUpdateDt ?? null,
      enrollmentDate: r.enrolledDt ?? null,
      terminationDate,
      brandCodes,
      brandsCsv,
      brandNames,
      brandName: brandNames,
      hasLiveSelections,
      _original: r, 
    };
  });
}

export function normalizeBacEnrollmentRows(api) {
    return (api.enrollments || []).map(e => {
        const { names: brandNames, codes: brandCodes, csv: brandsCsv } = normalizeBrands(e.brandName);

        const latestCancel = (e.productSelections ?? [])
            .map(p => p.cancelDate)
            .filter(d => !!d)
            .sort()
            .pop() ?? null;
    
        const terminationDate = e.isTerminatedDate ?? latestCancel ?? e.optOutDt ?? null;
        
        const hasLiveSelections = (e.productSelections ?? []).some(isSelectionActive);
    
        return {
          ...e,
          dealerId: e.dealerCd ?? '',
          programStatusCd: e.enrollmentStatusCd ?? '',
          lastUpdateDate: e.lastUpdateDt ?? null,
          enrollmentDate: e.enrolledDt ?? null,
          terminationDate,
          brandCodes,
          brandsCsv,
          brandNames,
          brandName: brandNames,
          hasLiveSelections,
          _original: e, 
        };
      });
}
